import type { HttpErrorResponse } from '@angular/common/http';
import { computed } from '@angular/core';
import type { FileUploadItem } from '@indutrust/shared-model';
import {
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { injectDispatch, on, withReducer } from '@ngrx/signals/events';
import type { Observable } from 'rxjs';
import { EMPTY, catchError, concat, concatMap, from, tap } from 'rxjs';
import { fileUploadEvents } from './file-upload.events';

type StagedFile = {
  error?: string;
  file: File;
  id: string;
  name: string;
  status: 'queued' | 'uploading' | 'success' | 'error';
};

type FileUploadState = {
  committing: boolean;
  pendingDeleteIds: string[];
  stagedFiles: StagedFile[];
};

const initialState: FileUploadState = {
  committing: false,
  pendingDeleteIds: [],
  stagedFiles: [],
};

export type FileUploadConfig<TUploaded> = {
  /**
   * Factory called during store init (inside `withMethods`) — inject your API
   * client here. The `store` argument is the accumulated store at that point,
   * so you can read any state added **before** `withFileUpload()` in the
   * composition chain. Cast it to the concrete store type as needed.
   */
  upload: (store: object) => (file: File) => Observable<TUploaded>;
  /**
   * Optional delete factory — same rules as `upload`. The `savedId` string
   * is the server-side ID queued via `stageDelete()`.
   */
  delete?: (store: object) => (savedId: string) => Observable<unknown>;
};

function deleteOne(
  id: string,
  deleteFn: ((savedId: string) => Observable<unknown>) | undefined
): Observable<unknown> {
  if (!deleteFn) {
    return EMPTY;
  }
  return deleteFn(id).pipe(catchError(() => EMPTY));
}

/**
 * NgRx Signal Store feature providing staged file-upload + delete management
 * with per-file status tracking (`queued → uploading → success | error`).
 *
 * Both `upload` and `delete` config factories are called during store
 * initialization (inside `withMethods`), so `inject()` works inside them —
 * follow the same pattern as `withCrudEntitiesEvents`.
 *
 * **Staged mode (modals):** call `stageFiles()` on selection, call `commit()`
 * on Save. The modal can gate its close on `uploadSettled()`.
 *
 * **Immediate mode (inline forms):** call `stageFiles()` then `commit()` in
 * the same handler.
 *
 * @example
 * ```typescript
 * export const MyStore = signalStore(
 *   withFileUpload<MyUploadedType>({
 *     upload: () => {
 *       const client = inject(MyApiClient);
 *       return (file) => client.upload(file);
 *     },
 *     delete: () => {
 *       const client = inject(MyApiClient);
 *       return (id) => client.delete(id);
 *     },
 *   }),
 * );
 * ```
 */
export function withFileUpload<TUploaded>(config: FileUploadConfig<TUploaded>) {
  return signalStoreFeature(
    withState<FileUploadState>(initialState),
    withComputed(({ committing, stagedFiles }) => ({
      /** True while `commit()` is running. */
      uploadCommitting: computed(() => committing()),
      /** True if any staged file is in `error` state. */
      uploadHasErrors: computed(() =>
        stagedFiles().some((f) => f.status === 'error')
      ),
      /**
       * True when all staged files have settled (success or error) and no
       * commit is in-flight. True by definition when there are no staged files.
       */
      uploadSettled: computed(
        () =>
          !committing() &&
          stagedFiles().every(
            (f) => f.status === 'success' || f.status === 'error'
          )
      ),
      /** Staged files mapped to `FileUploadItem` for the `idt-file-uploader` component. */
      uploadStagedItems: computed<FileUploadItem[]>(() =>
        stagedFiles().map((f) => ({
          error: f.error,
          id: f.id,
          name: f.name,
          saved: false,
          status: f.status,
        }))
      ),
    })),
    withReducer(
      on(fileUploadEvents.filesStaged, ({ payload: files }, state) => {
        const newItems: StagedFile[] = files.map((file) => ({
          file,
          id: crypto.randomUUID(),
          name: file.name,
          status: 'queued' as const,
        }));
        return {
          stagedFiles: [...state.stagedFiles, ...newItems],
        };
      }),
      on(fileUploadEvents.fileUnstaged, ({ payload: id }, state) => ({
        stagedFiles: state.stagedFiles.filter(
          (f) => f.id !== id || f.status === 'uploading'
        ),
      })),
      on(fileUploadEvents.deleteStaged, ({ payload: savedId }, state) => {
        if (state.pendingDeleteIds.includes(savedId)) {
          return {};
        }
        return {
          pendingDeleteIds: [...state.pendingDeleteIds, savedId],
        };
      }),
      on(fileUploadEvents.deleteUnstaged, ({ payload: savedId }, state) => ({
        pendingDeleteIds: state.pendingDeleteIds.filter((id) => id !== savedId),
      })),
      on(fileUploadEvents.fileRetried, ({ payload: id }, state) => ({
        stagedFiles: state.stagedFiles.map((f) =>
          f.id === id && f.status === 'error'
            ? { ...f, status: 'queued' as const, error: undefined }
            : f
        ),
      })),
      on(fileUploadEvents.uploadsReset, () => initialState),
      on(fileUploadEvents.commitStarted, () => ({ committing: true })),
      on(fileUploadEvents.commitCompleted, () => ({
        committing: false,
        pendingDeleteIds: [],
      })),
      on(
        fileUploadEvents.fileStatusChanged,
        ({ payload: { id, status, error } }, state) => ({
          stagedFiles: state.stagedFiles.map((f) =>
            f.id === id ? { ...f, status, error } : f
          ),
        })
      )
    ),
    withMethods((store) => {
      const dispatch = injectDispatch(fileUploadEvents);
      const uploadFn = config.upload(store);
      const deleteFn = config.delete?.(store);

      const patchFileStatus = (
        id: string,
        status: StagedFile['status'],
        error?: string
      ): void => {
        dispatch.fileStatusChanged({ id, status, error });
      };

      const uploadOne = (item: StagedFile): Observable<unknown> => {
        patchFileStatus(item.id, 'uploading');
        return uploadFn(item.file).pipe(
          tap(() => patchFileStatus(item.id, 'success')),
          catchError((err: HttpErrorResponse) => {
            patchFileStatus(item.id, 'error', err.message);
            return EMPTY;
          })
        );
      };

      return {
        /**
         * Stages one or more files as `queued`, assigning a random local ID
         * to each. Does not trigger upload — call `commit()` to upload.
         */
        stageFiles(files: File[]): void {
          dispatch.filesStaged(files);
        },

        /**
         * Removes a staged file by its local ID. No-op if the file is
         * currently `uploading`.
         */
        unstage(id: string): void {
          dispatch.fileUnstaged(id);
        },

        /**
         * Queues a saved file's server-side ID for deletion on the next
         * `commit()`. Idempotent.
         */
        stageDelete(savedId: string): void {
          dispatch.deleteStaged(savedId);
        },

        /**
         * Removes a saved file's server-side ID from the pending-delete list.
         */
        unstageDelete(savedId: string): void {
          dispatch.deleteUnstaged(savedId);
        },

        /**
         * Resets a single errored file back to `queued` so it can be
         * re-attempted on the next `commit()`.
         */
        retry(id: string): void {
          dispatch.fileRetried(id);
        },

        /**
         * Clears all staged files and pending deletes without uploading.
         * Useful when the modal is closed without saving.
         */
        resetUploads(): void {
          dispatch.uploadsReset();
        },

        /**
         * Commits all pending deletes (sequentially) then uploads all
         * `queued` staged files (sequentially). Each file's status is updated
         * in place. On per-file upload error the item enters `error` state and
         * the batch continues. Safe to call again after partial failures (only
         * `queued` files are processed).
         */
        commit(): void {
          if (store.committing()) {
            return;
          }

          const queued = store
            .stagedFiles()
            .filter((f) => f.status === 'queued');
          const deleteIds = [...store.pendingDeleteIds()];

          if (queued.length === 0 && deleteIds.length === 0) {
            return;
          }

          dispatch.commitStarted();

          const deletions$ = from(deleteIds).pipe(
            concatMap((id) => deleteOne(id, deleteFn))
          );
          const uploads$ = from(queued).pipe(
            concatMap((item) => uploadOne(item))
          );

          concat(deletions$, uploads$).subscribe({
            complete: () => {
              dispatch.commitCompleted();
            },
          });
        },
      };
    })
  );
}
